import { useEffect, useState } from "react";
import { enGB, sv } from "react-day-picker/locale";
import * as reactDayPickerStyles from "react-day-picker/style.module.css";

import { GeneralLangFactory } from "@bokio/lang";
import { languageNotifier } from "@bokio/lang/languageNotifier";
import { Day } from "@bokio/mobile-web-shared/core/model/model";
import { Config } from "@bokio/shared/config";
import { mergeClassNames } from "@bokio/utils/classes";

import { Button } from "../Button";

import type { PropsWithChildren } from "react";
import type { DayPickerProps, Matcher } from "react-day-picker";
import type { Locale } from "react-day-picker/locale";

import * as styles from "./useReactDayPickerStylingProps.scss";

const reactDayPickerClassNames: DayPickerProps["classNames"] = {
	...reactDayPickerStyles,
	root: mergeClassNames(reactDayPickerStyles.root, styles.root),
	day: mergeClassNames(reactDayPickerStyles.day, styles.day),
	day_button: mergeClassNames(reactDayPickerStyles.day_button, styles.day_button),
	disabled: mergeClassNames(reactDayPickerStyles.disabled, styles.disabled),
	weekday: mergeClassNames(reactDayPickerStyles.weekday, styles.weekday),
	outside: mergeClassNames(reactDayPickerStyles.outside, styles.outside),
	selected: mergeClassNames(reactDayPickerStyles.selected, styles.selected),
	today: mergeClassNames(reactDayPickerStyles.today, styles.today),
	months: mergeClassNames(reactDayPickerStyles.months, styles.months),
	month_caption: mergeClassNames(reactDayPickerStyles.month_caption, styles.month_caption),
	nav: mergeClassNames(reactDayPickerStyles.nav, styles.nav),
	button_previous: mergeClassNames(reactDayPickerStyles.button_previous, styles.button_previous),
	button_next: mergeClassNames(reactDayPickerStyles.button_next, styles.button_next),
	range_start: mergeClassNames(reactDayPickerStyles.range_start, styles.range_start),
	range_middle: mergeClassNames(reactDayPickerStyles.range_middle, styles.range_middle),
	range_end: mergeClassNames(reactDayPickerStyles.range_end, styles.range_end),
};

const removeChildren = <P extends PropsWithChildren<unknown>>(props: P): Omit<P, "children"> => {
	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	const { children, ...rest } = props;
	return rest;
};

const reactDayPickerModifiersClassNames = {
	hover_range_start: styles.modifier_hover_range_start,
	hover_range_end: styles.modifier_hover_range_end,
	hover_range_middle: styles.modifier_hover_range_middle,
} as const satisfies DayPickerProps["modifiersClassNames"];

const createReactDayPickerStylingProps = ({
	setCurrentShowingMonth,
	minMonth,
	maxMonth,
}: {
	setCurrentShowingMonth: (month: Day) => void;
	minMonth: Date | undefined;
	maxMonth: Date | undefined;
}): Partial<DayPickerProps> => {
	const generalLang = GeneralLangFactory();

	const reactDayPickerComponents: DayPickerProps["components"] = {
		// By default there's a Chevron component in the children, removing it here to replace the icon from CSS .button_previous, .button_next
		NextMonthButton: props => <button type="button" {...removeChildren(props)} />,
		PreviousMonthButton: props => {
			const today = Day.today();
			const isWithinMinMaxMonth = (day: Day) => {
				return (
					(minMonth?.getTime() ?? -Infinity) <= day.toDate().getTime() &&
					day.toDate().getTime() <= (maxMonth?.getTime() ?? Infinity)
				);
			};
			return (
				<>
					<Button
						appearance="text"
						disabled={!isWithinMinMaxMonth(today)}
						onClick={() => {
							setCurrentShowingMonth(today);
						}}
						// Aria label's text content matching the format prev/next month buttons.
						// Ref:
						// - https://github.com/gpbl/react-day-picker/blob/b55a8786b617a09158b9bd01ec80d75f9ce83618/packages/react-day-picker/src/locale/en-GB.ts
						// - https://github.com/gpbl/react-day-picker/blob/b55a8786b617a09158b9bd01ec80d75f9ce83618/packages/react-day-picker/src/locale/sv.ts
						aria-label={generalLang.DatePicker_TodayNavigationButton_AriaLabel}
					>
						{generalLang.Today}
					</Button>
					<button type="button" {...removeChildren(props)} />
				</>
			);
		},
	};

	return {
		components: reactDayPickerComponents,
		classNames: reactDayPickerClassNames,
		modifiersClassNames: reactDayPickerModifiersClassNames,
		weekStartsOn: 1,
		fixedWeeks: true,
		showOutsideDays: true,
	};
};

const mapLangCodeToLocale = (langCode: string): Locale => {
	return langCode.startsWith("sv") ? sv : enGB;
};

export type ReactDayPickerModifiers = Record<keyof typeof reactDayPickerModifiersClassNames, Matcher[]>;

export const castValidDayToDate = (day?: Day): Date | undefined => {
	if (day && day.isValid()) {
		return day.toDate();
	}

	return undefined;
};

export const castDateToDay = (date?: Date): Day | undefined => {
	if (date) {
		return Day.fromDate(date);
	}

	return undefined;
};

export const useReactDayPickerStylingProps = ({
	setCurrentShowingMonth,
	minMonth,
	maxMonth,
}: {
	setCurrentShowingMonth: (month: Day) => void;
	minMonth: Date | undefined;
	maxMonth: Date | undefined;
}) => {
	const [locale, setLocale] = useState(() => mapLangCodeToLocale(Config.env.lang));

	useEffect(() => {
		return languageNotifier.subscribe(lang => {
			setLocale(mapLangCodeToLocale(lang));
		});
	}, []);

	const reactDayPickerStylingProps = createReactDayPickerStylingProps({ setCurrentShowingMonth, minMonth, maxMonth });

	return {
		...reactDayPickerStylingProps,
		locale,
	};
};
